import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { DiagnosticsQuery } from './Diagnostics.types';
import {
  createDiagnostic,
  generateWorkOrderDiagnostic,
  getDiagnostic,
  listDiagnosticPats,
  listDiagnostics,
  removeDiagnostic,
  reportDiagnostic,
  updateDiagnostic,
} from './Diagnostics.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { diagnosticDicionaryError } from './Diagnostics.erros';

const QUERY_ID = 'diagnostics';

export function useDiagnostics(params?: DiagnosticsQuery, enabled = true) {
  const query = useInfiniteQuery(
    [QUERY_ID, params],
    ({ pageParam }) => {
      const req: DiagnosticsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listDiagnostics(req);
    },
    {
      enabled,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useDiagnosticParts(params?: DiagnosticsQuery, enabled = true) {
  const queryCLient = useQueryClient();
  const cache = queryCLient.getQueryData([
    'diagnostics',
    'parts',
    { ...params },
  ]);

  return useInfiniteQuery(
    ['diagnostics', 'parts', { ...params }],
    ({ pageParam }) => {
      const req: DiagnosticsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listDiagnosticPats(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: enabled && !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useDiagnosticDetail(diagnosticId?: number) {
  const query = useQuery(
    ['diagnostic', diagnosticId],
    () => getDiagnostic(diagnosticId),
    {
      enabled: !!diagnosticId,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useDiagnosticMutation() {
  const query = useMutation(getDiagnostic);

  useServiceError(query.error);

  return query;
}

export function useDiagnosticUpdate() {
  const queryClient = useQueryClient();
  const intl = useIntl().formatMessage;

  const mutation = useMutation(updateDiagnostic, {
    onSuccess(_, data) {
      queryClient.invalidateQueries(['diagnostic', data.id]);
      notification.success({
        message: intl({
          id: 'diagnostics.update.notification.success.title',
        }),
        description: intl({
          id: 'diagnostics.update.notification.success.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDiagnosticCreate() {
  const queryClient = useQueryClient();
  const intl = useIntl().formatMessage;

  const mutation = useMutation(createDiagnostic, {
    onSuccess() {
      queryClient.invalidateQueries(QUERY_ID);
      notification.success({
        message: intl({
          id: 'diagnostics.form.notification.success.title',
        }),
        description: intl({
          id: 'diagnostics.form.notification.success.description',
        }),
      });
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) => diagnosticDicionaryError[err] || err),
      });
    },
  });

  return mutation;
}

export function useDiagnosticRemove() {
  const queryClient = useQueryClient();
  const intl = useIntl().formatMessage;

  const mutation = useMutation((id: number) => removeDiagnostic(id), {
    onSuccess() {
      queryClient.invalidateQueries(QUERY_ID);

      notification.success({
        message: intl({
          id: 'diagnostics.remove.notification.success.title',
        }),
        description: intl({
          id: 'diagnostics.remove.notification.success.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDiagnosticReport() {
  const intl = useIntl().formatMessage;

  const mutation = useMutation((id: number) => reportDiagnostic(id), {
    onError() {
      notification.error({
        message: intl({
          id: 'diagnostics.modal.report.error.title',
        }),
        description: intl({
          id: 'diagnostics.modal.report.error.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDiagnosticGenerateWorkOrder() {
  const queryClient = useQueryClient();

  const mutation = useMutation(generateWorkOrderDiagnostic, {
    onSuccess() {
      queryClient.invalidateQueries(QUERY_ID);
      notification.success({
        message: 'Sucesso',
        description: 'Orçamento gerado com sucesso!',
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}
